import { TabItem, Tabs } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useNavigate } from 'react-router-dom';
import PageHeader from 'src/js/component/page-header';
import useLocalStorageWithState from 'src/js/component/useLocalStorageWithState';
import systemInfo from 'src/js/data/systemInfo';
import { compileWithArgs } from 'src/js/helper/path-to-regexp-helper';
import { useNavigateListener } from 'src/js/hook/use-history-listener';
import { useStores } from 'src/js/hook/use-stores';
import { MakitoPaths } from 'src/js/route';
import SidebarLayout from 'src/js/sidebar';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/constant';
import { MediaKey, MediaPageParameters } from './media-router';
import { useMutable } from 'src/js/hook/use-mutable';

export const Media: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();
  const [selectedTab, setSelectedTab] = useLocalStorageWithState(
    `${constant.lStorage.mediaTab}:${sessionStore.username}`,
    systemInfo.storage ? MediaKey.RECORDINGS : MediaKey.LOGOS,
  );

  const [tabs, setTabs] = useState<string[]>([]);
  const [mTabs, setMTabs] = useMutable<string[]>([]);
  const updateTabs = () => {
    let tabs = Object.values(MediaKey);

    // Check if recording is present
    if (!systemInfo.storage) {
      tabs = tabs.filter((v) => v !== MediaKey.RECORDINGS);
    }

    // Check if logo is licensed
    if (!systemInfo.hasLogo) {
      tabs = tabs.filter((v) => v !== MediaKey.LOGOS);
    }
    setTabs(tabs);
    setMTabs(tabs);
  };

  useEffect(() => {
    updateTabs();
  }, []);
  useWatchObject(systemInfo, updateTabs);

  useNavigateListener((newLocation) => {
    const routeName = '/media/:page';
    const matchedParam = matchPath(routeName, newLocation.pathname);
    // NKE-2600 Prevent people from directly accessing tabs that are currently unavailable (or from redirect after login)
    // console.log('matchedParam', matchedParam);
    // console.log('tabs', mTabs(), tabs);
    if (!isNil(matchedParam?.params) && !isNil(mTabs().find((t) => t === matchedParam.params?.page))) {
      setSelectedTab(matchedParam.params?.page as MediaKey);
    } else if (isNil(matchedParam?.params) && isNil(mTabs().find((t) => t === selectedTab))) {
      // current page specified in localStorage does not exists
      // console.log('404', matchedParam?.params, selectedTab);
      // console.log('tabs', mTabs(), tabs);
      if (systemInfo.hasLogo) {
        setSelectedTab(MediaKey.LOGOS);
        navigate('/media/logos');
      } else if (systemInfo.storage) {
        navigate('/media/recordings');
        setSelectedTab(MediaKey.RECORDINGS);
      } else {
        navigate('/streaming');
        setSelectedTab(undefined);
      }
    }
  });

  return (
    <SidebarLayout>
      <PageHeader title={t('sidebar.media')} iconName="Assets" />
      <Tabs
        selectedTab={selectedTab}
        onSelect={(event: ChangeEvent<{ eventkey: MediaKey }>) => {
          setSelectedTab(event.target.eventkey);
          navigate(
            compileWithArgs<MediaPageParameters>(MakitoPaths.media)({
              page: event.target.eventkey,
            }),
            { replace: true },
          );
        }}
      >
        {tabs.map(
          (tab): ReactNode => (
            <TabItem key={tab} caption={t(`media.tabs.${tab}`)} eventkey={tab} />
          ),
        )}
      </Tabs>
      <Outlet />
    </SidebarLayout>
  );
};
